import { useMuiMediaQuery } from '@/hooks/useMediaQuery';
import { roundNumber } from '@/utils/maths';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import json2mq from 'json2mq';
import { useRef, useState } from 'react';
import { NoData } from '../components';
import Head from '../components/Head';
import SkeletonLoader from '../components/SkeletonLoader';
import StackingOptions from '../components/StackingOptions';
import { useActivities } from '../hooks/useActivities';
import { useActivityZones } from '../hooks/useActivityZones';
import { useUserZones } from '../hooks/useUserZones';
import styles from '../style.module.css';
import { ActivityZone, Stacking } from '../types';
import { weekNumbers } from '../utils/activities';
import { blue, green, grey, orange, red, titleStyle } from '../utils/common';
import {
  formatWeekNumber,
  lastWeekNumber,
  maxWeeksToDisplay,
} from '../utils/date';

const getHeartRateZoneTime = (zones: ActivityZone[][], index: number) => {
  /** @todo remplacer le .map par un .reduce avec 52 valeurs (initialisées à 0) */
  const times = zones.map((zones) => {
    const heartrates = zones
      .filter((zone) => zone.type === 'heartrate')
      .map((zone) => zone.distribution_buckets);

    return heartrates[0] ? heartrates[0][index].time / 60 : 0;
  });

  return times.reduce((acc, time) => roundNumber(acc + time), 0);
};

const Zones = () => {
  const [stacking, setStacking] = useState<Stacking>(undefined);
  const { activities, isLoading: isActivitiesLoading } = useActivities();
  const { zones: activityZones, isLoading: isZonesLoading } =
    useActivityZones();
  const { zones: userZones } = useUserZones();
  const isMobile = useMuiMediaQuery(json2mq({ maxWidth: 600 }));

  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);

  const handleRadioChangeStacking = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    const newStacking = value === 'undefined' ? undefined : (value as Stacking);
    setStacking(newStacking);
  };

  if (isActivitiesLoading || isZonesLoading) {
    return <SkeletonLoader />;
  }

  if (!activities) {
    return <NoData>No activities</NoData>;
  }

  if (!activityZones) {
    return <NoData>No activity zones</NoData>;
  }

  const limit = lastWeekNumber - maxWeeksToDisplay;
  const shortenZones = (zones: number[]) => {
    return zones.slice(limit, lastWeekNumber);
  };

  const needsToBeShorten = isMobile && lastWeekNumber > maxWeeksToDisplay;

  let zones1 = activityZones.map((zones) => getHeartRateZoneTime(zones, 0));
  if (needsToBeShorten) {
    zones1 = shortenZones(zones1);
  }
  let zones2 = activityZones.map((zones) => getHeartRateZoneTime(zones, 1));
  if (needsToBeShorten) {
    zones2 = shortenZones(zones2);
  }
  let zones3 = activityZones.map((zones) => getHeartRateZoneTime(zones, 2));
  if (needsToBeShorten) {
    zones3 = shortenZones(zones3);
  }
  let zones4 = activityZones.map((zones) => getHeartRateZoneTime(zones, 3));
  if (needsToBeShorten) {
    zones4 = shortenZones(zones4);
  }
  let zones5 = activityZones.map((zones) => getHeartRateZoneTime(zones, 4));
  if (needsToBeShorten) {
    zones5 = shortenZones(zones5);
  }

  let xAxisLabels = weekNumbers.map((weekNumber) =>
    formatWeekNumber(weekNumber)
  );
  if (needsToBeShorten) {
    xAxisLabels = xAxisLabels.slice(limit + 1, lastWeekNumber + 1);
  }

  const heartRateZones = userZones?.heart_rate.zones;

  const options = {
    chart: {
      type: 'column',
    },
    title: {
      text: 'Durée par zones cardiaques',
      style: titleStyle,
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories: xAxisLabels,
      title: {
        text: 'week',
      },
    },
    yAxis: {
      title: {
        text: 'time (min)',
      },
      stackLabels: {
        enabled: true,
      },
    },
    plotOptions: {
      column: {
        stacking,
      },
    },
    series: [
      {
        name: heartRateZones
          ? `zone 1 (${heartRateZones[0].min} - ${heartRateZones[0].max} bpm)`
          : 'zone 1',
        data: zones1,
        color: grey,
      },
      {
        name: heartRateZones
          ? `zone 2 (${heartRateZones[1].min} - ${heartRateZones[1].max} bpm)`
          : 'zone 2',
        data: zones2,
        color: blue,
      },
      {
        name: heartRateZones
          ? `zone 3 (${heartRateZones[2].min} - ${heartRateZones[2].max} bpm)`
          : 'zone 3',
        data: zones3,
        color: green,
      },
      {
        name: heartRateZones
          ? `zone 4 (${heartRateZones[3].min} - ${heartRateZones[3].max} bpm)`
          : 'zone 4',
        data: zones4,
        color: orange,
      },
      {
        name: heartRateZones
          ? `zone 5 (${heartRateZones[4].min} bpm ...)`
          : 'zone 5',
        data: zones5,
        color: red,
      },
    ],
  };

  return (
    <div id="zones">
      <Head title="Strava Dashboard - zones" />
      <StackingOptions
        stacking={stacking}
        handleRadioChangeStacking={handleRadioChangeStacking}
      />
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartComponentRef}
        containerProps={{ className: styles.chartContainer }}
      />
    </div>
  );
};

export default Zones;
